export type ClubAccessRole =
  | 'administrator'
  | 'head_coach'
  | 'assistant_coach'
  | 'staff'
  | 'player';

export type ClubStaffMember = {
  name: string;
  role: ClubAccessRole;
  linkedPlayerName?: string;
  invitationStatus: 'active' | 'pending' | 'slot';
};

export const CLUB_STAFF: ClubStaffMember[] = [
  { name: 'Tommy Poloczek', role: 'administrator', invitationStatus: 'active' },
  { name: 'Dominik Lichon', role: 'head_coach', linkedPlayerName: 'Dominik Lichon', invitationStatus: 'active' },
  { name: 'Marcin Śliwa', role: 'head_coach', linkedPlayerName: 'Marcin Śliwa', invitationStatus: 'active' },
  { name: 'Assistant Coach Slot', role: 'assistant_coach', invitationStatus: 'slot' },
];

export const ADMINISTRATOR_NAME = 'Tommy Poloczek';

export function staffForPlayer(playerName: string) {
  const normalise = (value: string) =>
    value.trim().toLowerCase().normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '').replace(/ł/g, 'l').replace(/\s+/g, ' ');

  return CLUB_STAFF.find(
    (staff) => staff.linkedPlayerName && normalise(staff.linkedPlayerName) === normalise(playerName),
  );
}

export function normalizeMembershipRole(role?: string | null): ClubAccessRole {
  const value = String(role || '').trim().toLowerCase().replace(/[\s-]+/g, '_');

  if (['administrator', 'admin', 'owner', 'developer', 'super_admin'].includes(value)) return 'administrator';
  // Existing production DB roles are owner + assistant.
  // Keep `assistant` mapped to Head Coach to preserve current accounts.
  if (['assistant', 'head_coach', 'headcoach'].includes(value)) return 'head_coach';
  if (['coach_assistant', 'assistant_coach', 'co_coach'].includes(value)) return 'assistant_coach';
  if (['staff', 'team_staff', 'operations_staff'].includes(value)) return 'staff';
  if (['player', 'squad', 'team_player'].includes(value)) return 'player';
  return 'head_coach';
}

export function roleLabel(role: ClubAccessRole) {
  if (role === 'administrator') return 'Administrator';
  if (role === 'assistant_coach') return 'Assistant Coach';
  if (role === 'staff') return 'Staff';
  if (role === 'player') return 'Player';
  return 'Head Coach';
}

export function canManageAccess(role?: string | null) {
  return normalizeMembershipRole(role) === 'administrator';
}

export function canCoach(role?: string | null) {
  return ['administrator', 'head_coach', 'assistant_coach'].includes(normalizeMembershipRole(role));
}

export function canViewSensitiveIntelligence(role?: string | null) {
  return canCoach(role);
}

export function isPlayerRole(role?: string | null) {
  return normalizeMembershipRole(role) === 'player';
}

export function isStaffRole(role?: string | null) {
  return normalizeMembershipRole(role) === 'staff';
}

export function usesSanitizedPlayerData(role?: string | null) {
  return isPlayerRole(role) || isStaffRole(role);
}

export const PLAYER_ALLOWED_MODULES = new Set([
  'dashboard', 'operations', 'players', 'matchday', 'league', 'news', 'settings',
]);

export const STAFF_ALLOWED_MODULES = new Set([
  'dashboard',
  'operations',
  'players',
  'matches',
  'matchday',
  'training',
  'league',
  'news',
  'settings',
]);

export function moduleAllowedForRole(moduleId: string, role?: string | null) {
  if (isPlayerRole(role)) return PLAYER_ALLOWED_MODULES.has(moduleId);
  if (isStaffRole(role)) return STAFF_ALLOWED_MODULES.has(moduleId);
  return true;
}
